// Swarm telemetry — the coordinator block streams jobs/{runId}/swarm/{docId}
// (backend/shared/blocks/swarmFeed.js). Live hooks here; the scripted demo
// replay lives in swarmDemo.js and folds through the same reducer.
import { useEffect, useRef, useState } from 'react';
import firebase, { db } from '../firebaseConfig';
import { foldSwarmEvent } from './swarmFeed';

const ready = () => firebase.apps.length > 0 && db;

// Every swarm doc of one run (a run with pipeline.call fan-out has several:
// one per coordinator per call). Ordered as they started.
export function useSwarmDocs(jobId) {
  const [docs, setDocs] = useState([]);
  useEffect(() => {
    if (!ready() || !jobId) { setDocs([]); return undefined; }
    return db.collection('jobs').doc(jobId).collection('swarm').onSnapshot(
      (snap) => {
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        items.sort((a, b) => (a.startedAt || 0) - (b.startedAt || 0));
        setDocs(items);
      },
      () => setDocs([]),
    );
  }, [jobId]);
  return docs;
}

// Recent pipeline runs of this org that had at least one coordinator
// (jobs/{id}.swarmIds is set by the block). Newest first; `active` first.
export function useOrgSwarmJobs(orgId, max = 12) {
  const [jobs, setJobs] = useState(null);
  useEffect(() => {
    if (!ready() || !orgId) { setJobs(null); return undefined; }
    return db.collection('jobs')
      .where('spec.orgId', '==', orgId)
      .onSnapshot(
        (snap) => {
          const items = snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((j) => Array.isArray(j.swarmIds) && j.swarmIds.length);
          items.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
          setJobs(items.slice(0, max));
        },
        () => setJobs([]),
      );
  }, [orgId, max]);
  return jobs;
}

// Replay a scripted event list ({ at: msOffset, ...event }) through the
// same fold the backend uses, in real time, looping with a hold at the
// end. Reduced motion → jump straight to the finished doc.
export function useSwarmReplay(script, { loop = true, holdMs = 6000, speed = 1, enabled = true } = {}) {
  const [doc, setDoc] = useState(null);
  const timers = useRef([]);
  useEffect(() => {
    if (!enabled || !script?.length) { setDoc(null); return undefined; }
    const reduce = typeof window !== 'undefined'
      && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setDoc(script.reduce((d, e) => foldSwarmEvent(d, e), null));
      return undefined;
    }
    let cancelled = false;
    const clear = () => { timers.current.forEach(clearTimeout); timers.current = []; };
    const play = () => {
      clear();
      let acc = null;
      setDoc(null);
      const t0 = script[0].at || 0;
      const shift = Date.now() - t0; // replay timestamps read as "now", so elapsed counts up
      script.forEach((evt) => {
        timers.current.push(setTimeout(() => {
          if (cancelled) return;
          acc = foldSwarmEvent(acc, { ...evt, at: evt.at + shift });
          setDoc({ ...acc, rounds: acc.rounds.map((r) => ({ ...r, agents: r.agents.map((a) => ({ ...a })) })) });
        }, (evt.at - t0) / speed));
      });
      if (loop) {
        const end = (script[script.length - 1].at - t0) / speed + holdMs;
        timers.current.push(setTimeout(() => { if (!cancelled) play(); }, end));
      }
    };
    play();
    return () => { cancelled = true; clear(); };
  }, [script, loop, holdMs, speed, enabled]);
  return doc;
}
